import { useState } from 'react';

import { fetchSaveUploadedDocuments } from '@api/lendingAPI/LendingAPI';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    IconCircleError,
    IconFilePlusInside,
    IconFileWithItemsOutline,
    IconUpload
} from '@iwoca/icons';
import { Button } from '@iwoca/orion-ui';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import styles from './UploadBankStatementsForVerification.module.css';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import { TDocument } from '../../../uploadDocumentsModal/UploadDocumentsModal.reducer';
import { serialiseDocument } from '../../../uploadDocumentsModal/UploadDocumentsModalHelper';
import { TNewBankAccountModalName } from '../verifyNewBankAccountModal/VerifyNewBankAccountModal';

const MAX_FILE_SIZE = 52428800; // 50MB

export const UploadBankStatementsForVerification = ({
    changeVerificationMethod,
    onClose
}: {
    changeVerificationMethod: (method: TNewBankAccountModalName) => void;
    onClose: () => void;
}) => {
    const stateKey = useStateKey();

    const [uploadedDocumentStatuses, setUploadedDocumentStatuses] = useState<
        TDocument[]
    >([]);

    const bankStatementType = {
        label: 'bankStatement',
        value: 'bank statement'
    };

    const onDrop = async (acceptedFiles: File[]) => {
        tracking.ampli.addBankDetailsBankStatementUploaded();
        await Promise.all(
            acceptedFiles.map(async (file) => {
                try {
                    const serialisedDoc = serialiseDocument(
                        file,
                        bankStatementType.value
                    );

                    const response = await fetchSaveUploadedDocuments({
                        impersonatingStateKey: stateKey,
                        body: serialisedDoc
                    });

                    const code = response[1].ok
                        ? response[1].status
                        : response[0].errors[0].code;

                    const completedUpload = {
                        type: bankStatementType,
                        name: file.name,
                        pending: false,
                        code: code,
                        success: response[1].ok
                    };

                    setUploadedDocumentStatuses((prev) => [
                        ...prev,
                        completedUpload
                    ]);
                } catch {
                    setUploadedDocumentStatuses((prev) => [
                        ...prev,
                        {
                            type: bankStatementType,
                            name: file.name,
                            pending: false,
                            success: false,
                            error: true
                        }
                    ]);
                }
            })
        );
    };
    return (
        <>
            <p className={styles.guidanceListHeader}>
                <FormattedMessage
                    id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.heading"
                    defaultMessage="Upload a recent bank statement"
                />
            </p>
            <ul className={styles.guidanceList}>
                <li>
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.guidance_1"
                        defaultMessage="The statement must clearly show your name to confirm the account holder."
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.guidance_2"
                        defaultMessage="The bank statement must be from the last 90 days (e.g. your most recent monthly statement)."
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.guidance_3"
                        defaultMessage="Upload files in PDF format (max. 50MB)."
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.guidance_4"
                        defaultMessage="Ensure that the files are not password protected (or provide us with the password)."
                    />
                </li>
                <li>
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.guidance_5"
                        defaultMessage="You can download the bank statements from your online banking."
                    />
                </li>
            </ul>
            <Dropzone
                accept={{
                    'application/pdf': ['.pdf']
                }}
                maxSize={MAX_FILE_SIZE}
                onDrop={onDrop}
            >
                {({ open, getRootProps, getInputProps }) => (
                    <div
                        className={styles.dragAndDropField}
                        {...getRootProps({
                            onClick: (evt) => evt.stopPropagation()
                        })}
                    >
                        <input
                            type="file"
                            {...getInputProps()}
                            aria-label="upload-area"
                        />
                        <Button
                            className={styles.dragAndDropLink}
                            variant="tertiary"
                            onClick={() => {
                                open();
                            }}
                        >
                            <span className={styles.uploadLink}>
                                <IconUpload />
                                <FormattedMessage
                                    id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.upload"
                                    defaultMessage="Upload"
                                />
                            </span>
                            <span>
                                <FormattedMessage
                                    id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.drag_and_drop"
                                    defaultMessage="or drag and drop files here"
                                />
                            </span>
                        </Button>
                    </div>
                )}
            </Dropzone>
            {!!uploadedDocumentStatuses &&
                uploadedDocumentStatuses.length > 0 && (
                    <div className={styles.documentStatusContainer}>
                        {uploadedDocumentStatuses.map((document, idx) => (
                            <DocumentUploadStatusItem
                                key={`${document.name}-${idx}`}
                                document={document}
                            />
                        ))}
                    </div>
                )}
            <div className={styles.buttonContainer}>
                <Button
                    onClick={() => {
                        changeVerificationMethod(null);
                        onClose();
                    }}
                    variant="primary"
                    className={styles.button}
                    disabled={
                        !uploadedDocumentStatuses ||
                        uploadedDocumentStatuses.length === 0
                    }
                >
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.confirm_account_button"
                        defaultMessage="Confirm account"
                    />
                </Button>
                <Button
                    onClick={() => changeVerificationMethod(null)}
                    variant="tertiary"
                    className={styles.button}
                >
                    <FormattedMessage
                        id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.back_button"
                        defaultMessage="Back"
                    />
                </Button>
            </div>
        </>
    );
};

const DocumentUploadStatusItem = ({
    document
}: {
    document: TDocument;
    key: string;
}) => {
    if (document.success) {
        return (
            <div className={styles.documentStatus}>
                <div className={styles.documentStatusBar}>
                    <div className={styles.documentStatusBarCaption}>
                        <IconFileWithItemsOutline className={styles.fileIcon} />
                        <p className={styles.documentName}>{document.name}</p>
                    </div>
                    <div className={styles.documentStatusBarIcon}>
                        <IconFilePlusInside className={styles.fileIcon} />
                        <p className={styles.statusText}>
                            <FormattedMessage
                                id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.upload_successful"
                                defaultMessage="Uploaded"
                            />
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.documentStatus}>
            <div className={styles.documentStatusBar}>
                <div className={styles.documentStatusBarCaption}>
                    <IconFileWithItemsOutline className={styles.fileIcon} />
                    <p className={styles.documentName}>{document.name}</p>
                </div>

                <div className={styles.documentStatusBarIcon}>
                    <IconCircleError className={styles.fileIcon} />
                    <p className={styles.statusText}>
                        <FormattedMessage
                            id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.upload_error"
                            defaultMessage="Error"
                        />
                    </p>
                </div>
            </div>
            <div className={styles.errorStatusContainer}>
                {document.code === 'DuplicateDocumentError' ? (
                    <p className={styles.errorDetail}>
                        <FormattedMessage
                            id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.duplicate_document_error_detail"
                            defaultMessage="The submitted document already exists"
                        />
                    </p>
                ) : (
                    <p className={styles.errorDetail}>
                        <FormattedMessage
                            id="_.funding_requirements.add_payment_method.manual_bs_upload_verification.document_upload_error"
                            defaultMessage="Document upload failed. Please try again later."
                        />
                    </p>
                )}
            </div>
        </div>
    );
};
